import { isEqual } from "es-toolkit";
import { atom } from "jotai";
import { atomFamily } from "jotai/utils";
import { Fragment, type Node, Slice } from "prosemirror-model";
import type { EditorState } from "prosemirror-state";

import { Logger } from "@moment/logging";
import { schema } from "@moment/prosemirror-schema";
import { createCellID } from "@moment/prosemirror-utils";

import { $editorState } from "../prosemirror/editor-state.atom";
import { editorStateKeyForDocsRoute } from "../prosemirror/editorStateKey";
import { $flushCompilation } from "./cell-compiler.atom";

const logger = new Logger("cell-compiler");

// ============================================================================
// Types
// ============================================================================

export interface CompiledCells<T> {
	[key: string]: T;
}

/**
 * A compiler function that takes compiles all the compute cells in a ProseMirror `EditorState` that
 * have changed or been added. To do this, it takes the `EditorState` object and the current set of
 * compiled cells (the "previous results" of the compilation step), and produces a new set of
 * compiled cells.
 *
 * packages/state is type-agnostic - the actual types are defined in packages/app.
 */
export type CellCompilerFn<T> = (
	editorState: EditorState,
	previousResult: CompiledCells<T>
) => Promise<CompiledCells<T>>;

// ============================================================================
// Helpers
// ============================================================================

/**
 * Find a compute cell by ID in the document.
 *
 * Returns the node and its position, or null if not found.
 */
const inefficientlyFindComputeCell = (
	editorState: EditorState,
	cellId: string
): { node: Node; pos: number } | null => {
	let result: { node: Node; pos: number } | null = null;
	editorState.doc.descendants((node, pos) => {
		if (result) {
			return false;
		}

		if (node.type === schema.nodes.compute_cell && node.attrs["id"] === cellId) {
			result = { node, pos };
			return false;
		}

		return true;
	});

	return result;
};

/**
 * Checks whether a string is a valid JavaScript identifier.
 *
 * This is a notoriously hard problem, see [1]. We do it the slow way since we know this is not on
 * the critical path.
 *
 * [1]: https://stackoverflow.com/questions/2008279/validate-a-javascript-function-name
 */
const isValidJavaScriptIdentifier = (potentialIdentifier: string): boolean => {
	if (/^\s|\s$/.test(potentialIdentifier)) {
		return false;
	}

	try {
		new Function(`var ${potentialIdentifier}`);
	} catch {
		return false;
	}
	return true;
};

// ============================================================================
// Commit Cell Draft
// ============================================================================

/**
 * Writable atom to commit a cell's draft code to the document.
 *
 * This replaces the content of a compute_cell node with the given code draft,
 * then triggers an immediate compilation (flush).
 *
 * Use this when the code editor commits its draft content back to the ProseMirror document.
 */
export const $commitCellDraft = atomFamily((key: { documentId: string; pageId: string }) => {
	const commitAtom = atom(
		null,
		(get, set, { cellId, codeDraft }: { cellId: string; codeDraft: string }) => {
			const editorStateKey = editorStateKeyForDocsRoute(key);
			const editorState = get($editorState(editorStateKey));
			if (!editorState) {
				logger.error("Cannot commit cell draft: no editor state", key);
				return;
			}

			const cell = inefficientlyFindComputeCell(editorState, cellId);
			if (!cell) {
				logger.error("Cannot commit cell draft: cell not found", { ...key, cellId });
				return;
			}

			const { node, pos } = cell;
			const updatedComputeCell = schema.nodes.compute_cell.createAndFill(
				node.attrs,
				schema.text(codeDraft)
			);
			const tr = editorState.tr.replace(
				pos,
				pos + node.nodeSize,
				new Slice(Fragment.from(updatedComputeCell), 0, 0)
			);

			// Apply the transaction.
			set($editorState(editorStateKey), tr);

			// Flush compilation immediately.
			set($flushCompilation(key), true);
		}
	);
	commitAtom.debugLabel = `commitCellDraft/${key.documentId}/${key.pageId}`;
	return commitAtom;
}, isEqual);

// ============================================================================
// Rerun Compute Cell
// ============================================================================

/**
 * Writable atom to rerun a compute cell by replacing it with a new cell ID.
 *
 * This triggers re-compilation and re-execution without changing the cell's code.
 * We change the cell ID so the compiler sees it as a new cell.
 *
 * Use this when the user explicitly requests to rerun a cell (e.g., via Run button or hotkey).
 *
 * Returns the new cell ID so the caller can update any references.
 */
export const $rerunComputeCell = atomFamily((key: { documentId: string; pageId: string }) => {
	const rerunAtom = atom(null, (get, set, cellId: string): string | undefined => {
		const editorStateKey = editorStateKeyForDocsRoute(key);
		const editorState = get($editorState(editorStateKey));

		if (!editorState) {
			logger.error("Cannot rerun compute cell: no editor state", key);
			return undefined;
		}

		const cell = inefficientlyFindComputeCell(editorState, cellId);
		if (!cell) {
			logger.error("Cannot rerun compute cell: cell not found", { ...key, cellId });
			return undefined;
		}

		const { node, pos } = cell;
		const newCellId = createCellID();
		const newAttrs = { ...node.attrs, id: newCellId };
		const updatedComputeCell = schema.nodes.compute_cell.createAndFill(newAttrs, node.content);
		const tr = editorState.tr.replace(
			pos,
			pos + node.nodeSize,
			new Slice(Fragment.from(updatedComputeCell), 0, 0)
		);

		// Apply the transaction.
		set($editorState(editorStateKey), tr);

		// Flush compilation immediately.
		set($flushCompilation(key), true);

		return newCellId;
	});
	rerunAtom.debugLabel = `rerunComputeCell/${key.documentId}/${key.pageId}`;
	return rerunAtom;
}, isEqual);

// ============================================================================
// Rename Compute Cell
// ============================================================================

/**
 * Writable atom to rename a compute cell.
 *
 * Updates the cellName attribute of the compute cell node.
 * Returns an error message if the name is invalid, or undefined on success.
 *
 * Use this when the user renames a cell via the UI.
 */
export const $renameComputeCell = atomFamily((key: { documentId: string; pageId: string }) => {
	const renameAtom = atom(
		null,
		(
			get,
			set,
			{ cellId, newCellName }: { cellId: string; newCellName: string }
		): { error: string } | undefined => {
			if (!isValidJavaScriptIdentifier(newCellName)) {
				return { error: "Invalid JavaScript identifier" };
			}

			const editorStateKey = editorStateKeyForDocsRoute(key);
			const editorState = get($editorState(editorStateKey));

			if (!editorState) {
				logger.error("Cannot rename compute cell: no editor state", key);
				return { error: "No editor state" };
			}

			const cell = inefficientlyFindComputeCell(editorState, cellId);
			if (!cell) {
				logger.error("Cannot rename compute cell: cell not found", { ...key, cellId });
				return { error: "Cell not found" };
			}

			const { node, pos } = cell;
			const tr = editorState.tr.setNodeMarkup(pos, null, {
				...node.attrs,
				cellName: newCellName,
			});

			// Apply the transaction.
			set($editorState(editorStateKey), tr);

			// Flush compilation immediately
			set($flushCompilation(key), true);

			return undefined;
		}
	);
	renameAtom.debugLabel = `renameComputeCell/${key.documentId}/${key.pageId}`;
	return renameAtom;
}, isEqual);
